import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { AlertCircle, MessageSquare, Calendar } from 'lucide-react';
import { usePersonStore } from '../store/personStore';
import { useAuthStore } from '../store/authStore';
import { getBookUserId, isAssistantUser } from '../lib/actingFor';
import { useGmailStore } from '../store/gmailStore';
import api, { coachMoveFlagsApi, personMergesApi } from '../lib/api';
import { useActivityStore } from '../store/activityStore';
import {
  PersonHeader,
  PersonContactInfo,
  PersonPrivateNote,
  PersonPipelineStatus,
  PersonOrganizations,
  PersonForm,
  LinkOrganizationModal,
  PersonTasks,
  PersonBlastStatus,
  ConvertToBlastModal,
  CoachNoLongerHereSheet,
} from '../components/people';
import { MergePersonSheet } from '../components/people/MergePersonSheet';
import { MergedFromSection } from '../components/people/MergedFromSection';
import { ComposeEmailSheet } from '../components/people/ComposeEmailSheet';
import { GmailFallbackDialog } from '../components/people/GmailFallbackDialog';
import { useGmailLaunch } from '../hooks/useGmailLaunch';
import { AssignmentInfoBanner } from '../components/shared/AssignmentInfoBanner';
import { RequestCoachButton } from '../components/requests';
import { TagChips, TagPicker } from '../components/tags';
import { useTagStore } from '../store/tagStore';
import { effectiveFirstName, effectiveLastName, callableNumbers, effectiveEmail } from '../lib/personPrecedence';
import { AddActivityModal, ActivityTimeline, ScheduleActivityModal, UpcomingActivities } from '../components/activities';
import type { Activity, CoachMoveFlagItem, CoachMoveNotifyItem, PersonMergeItem, Temperature } from '../types';
import { CHANNEL_LABELS } from '../types/list';

/**
 * Person detail page
 * Shows full person info, organizations, activities, and tasks
 */
export const PersonDetail = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const {
    currentPerson,
    isLoadingDetail,
    detailError,
    fetchPerson,
    clearCurrentPerson,
  } = usePersonStore();

  const { fetchActivities, fetchUpcoming, cancelActivity } = useActivityStore();
  const { user: currentUser, hasAnyRole } = useAuthStore();
  const { canSend, fetchStatus } = useGmailStore();

  // Gmail send-capability — needed to gate ComposeEmailSheet's send UI.
  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  // Tags (Org & Person Tags v1) — pills + add-control in the header.
  const { byRecord, loadForRecord, remove } = useTagStore();
  useEffect(() => { if (currentPerson?.id) loadForRecord('PERSON', currentPerson.id).catch(() => {}); }, [currentPerson?.id, loadForRecord]);
  const personTags = currentPerson ? (byRecord[`PERSON:${currentPerson.id}`] ?? []) : [];

  // Modal states
  const [isEditFormOpen, setIsEditFormOpen] = useState(false);
  const [isActivityModalOpen, setIsActivityModalOpen] = useState(false);
  const [isLinkOrgModalOpen, setIsLinkOrgModalOpen] = useState(false);
  const [isConvertModalOpen, setIsConvertModalOpen] = useState(false);
  const [isScheduleModalOpen, setIsScheduleModalOpen] = useState(false);
  const [completingActivity, setCompletingActivity] = useState<Activity | null>(null);
  const [isComposeOpen, setIsComposeOpen] = useState(false);
  const [isFlagSheetOpen, setIsFlagSheetOpen] = useState(false);
  const [personFlags, setPersonFlags] = useState<CoachMoveFlagItem[]>([]);
  const [openNotify, setOpenNotify] = useState<CoachMoveNotifyItem | null>(null);
  const [flagsLoaded, setFlagsLoaded] = useState(false);

  const refreshFlags = useCallback(async () => {
    if (!id) return;
    try {
      const { flags, openNotify: notify } = await coachMoveFlagsApi.forPerson(id);
      setPersonFlags(flags);
      setOpenNotify(notify ?? null);
    } catch { /* marker is best-effort */ } finally {
      setFlagsLoaded(true);
    }
  }, [id]);

  useEffect(() => { refreshFlags(); }, [refreshFlags]);

  const [personMerges, setPersonMerges] = useState<PersonMergeItem[]>([]);
  const refreshMerges = useCallback(async () => {
    if (!id) return;
    try { const { merges } = await personMergesApi.list({ personId: id }); setPersonMerges(merges); } catch { setPersonMerges([]); }
  }, [id]);
  useEffect(() => { refreshMerges(); }, [refreshMerges]);
  const [isMergeSheetOpen, setIsMergeSheetOpen] = useState(false);

  // ?coachFlag=1 deep link (from the heads-up task's "Review & make the change")
  // auto-opens the sheet once the flags feed has loaded, then strips the param.
  const [searchParams, setSearchParams] = useSearchParams();
  const coachFlagHandled = useRef(false);
  useEffect(() => {
    if (coachFlagHandled.current) return;
    if (searchParams.get('coachFlag') !== '1') return;
    if (!currentPerson || !flagsLoaded) return;
    coachFlagHandled.current = true;
    setIsFlagSheetOpen(true);
    const next = new URLSearchParams(searchParams);
    next.delete('coachFlag');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams, currentPerson, flagsLoaded]);

  // Tombstone redirect (CRM-144 UX §5): a loser URL 200s with
  // { person: null, mergedInto }. Bounce to the survivor with a toast
  // explaining what happened. Hook must sit above the early returns below.
  const mergedInto = usePersonStore((s) => s.mergedInto);
  // Guards against StrictMode's double-invoked effects (and any re-fire
  // before mergedInto is reset) toasting/navigating more than once for the
  // same tombstone. Keyed by the SOURCE id (the tombstone URL's own :id from
  // useParams), not the destination survivor's id — the /people/:id route has
  // no `key`, so the page stays mounted across navigations, and a second,
  // different tombstone can merge into the same survivor. Keying by the
  // survivor id would make the guard bail on that second redirect and strand
  // the page on the loading spinner.
  const redirectedFromRef = useRef<string | null>(null);
  useEffect(() => {
    if (!currentPerson && mergedInto?.id) {
      if (redirectedFromRef.current === id) return;
      redirectedFromRef.current = id ?? null;
      toast(
        `This record was merged into ${mergedInto.name ?? 'another person'}${mergedInto.mergedAt ? ` on ${new Date(mergedInto.mergedAt).toLocaleDateString()}` : ''}${mergedInto.by ? ` by ${mergedInto.by}` : ''}`,
        { icon: '🔀', duration: 8000, id: 'merged-redirect' }
      );
      navigate(`/people/${mergedInto.id}`, { replace: true });
    }
  }, [currentPerson, mergedInto, navigate, id]);

  // Gmail launcher (GMAIL compose mode) — hooks must run unconditionally on
  // every render, so this sits above the `!currentPerson` early-return below.
  // The null-guard keeps effectiveEmail() from being called before
  // currentPerson has loaded.
  const googleEmail = useGmailStore((s) => s.googleEmail);
  const contactEmail = currentPerson ? (effectiveEmail(currentPerson) ?? '') : '';
  const gmailLaunch = useGmailLaunch(contactEmail, googleEmail);

  // Fetch person on mount
  useEffect(() => {
    if (id) {
      fetchPerson(id);
      // Record view (fire-and-forget)
      api.post('/api/views', { personId: id }).catch(() => {});
    }

    // Cleanup on unmount
    return () => {
      clearCurrentPerson();
      // The redirect guard above is per-visit, not per-session: leaving this
      // id (unmount, or navigating to any other id — including Back to this
      // same id later) must clear it, otherwise revisiting a tombstone whose
      // id happens to match the last-redirected-from id silently no-ops and
      // strands the page on the loading spinner.
      redirectedFromRef.current = null;
    };
  }, [id, fetchPerson, clearCurrentPerson]);

  // Handle edit button click
  const handleEdit = () => {
    setIsEditFormOpen(true);
  };

  // Handle edit form close
  const handleEditClose = () => {
    setIsEditFormOpen(false);
  };

  // Handle edit success - refresh the person data
  const handleEditSuccess = () => {
    setIsEditFormOpen(false);
    if (id) {
      fetchPerson(id);
    }
  };

  // Handle log activity button click
  const handleLogActivity = () => {
    setIsActivityModalOpen(true);
  };

  // Handle activity modal close
  const handleActivityModalClose = () => {
    setIsActivityModalOpen(false);
  };

  // Handle activity created - refresh person data and upcoming
  const handleActivityCreated = (
    updatedTemperature: Temperature | null,
    updatedLastActivityAt: string | null
  ) => {
    if (id) {
      fetchPerson(id);
      fetchUpcoming(id);
    }
    setCompletingActivity(null);
  };

  // Handle link organization button click
  const handleLinkOrganization = () => {
    setIsLinkOrgModalOpen(true);
  };

  // Handle link org modal close
  const handleLinkOrgModalClose = () => {
    setIsLinkOrgModalOpen(false);
  };

  // Handle organization links changed (after link/edit/delete)
  const handleLinksChanged = () => {
    if (id) {
      fetchPerson(id);
    }
  };

  // Handle convert to Blast
  const handleConvert = () => {
    setIsConvertModalOpen(true);
  };

  const handleConvertSuccess = () => {
    setIsConvertModalOpen(false);
    if (id) {
      fetchPerson(id);
    }
  };

  // Loading state
  if (isLoadingDetail) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blast-600 mx-auto"></div>
          <p className="mt-4 text-gray-500 dark:text-gray-400">Loading person...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (detailError) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <AlertCircle className="h-12 w-12 text-red-400 mx-auto mb-4" />
          <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">Error loading person</h2>
          <p className="text-gray-500 dark:text-gray-400 mb-4">{detailError}</p>
          <button
            onClick={() => navigate('/people')}
            className="btn-secondary"
          >
            Back to People
          </button>
        </div>
      </div>
    );
  }

  // Not found state — but a tombstone (mergedInto set) is not "not found":
  // the redirect effect above fires on this same render and navigates away.
  // Show the loading spinner instead of the "doesn't exist" card so the user
  // never sees a false not-found flash before the bounce to the survivor.
  if (!currentPerson) {
    if (mergedInto?.id) {
      return (
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blast-600 mx-auto"></div>
            <p className="mt-4 text-gray-500 dark:text-gray-400">Loading person...</p>
          </div>
        </div>
      );
    }
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <AlertCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">Person not found</h2>
          <p className="text-gray-500 dark:text-gray-400 mb-4">The person you're looking for doesn't exist or has been deleted.</p>
          <button
            onClick={() => navigate('/people')}
            className="btn-secondary"
          >
            Back to People
          </button>
        </div>
      </div>
    );
  }

  // Predicate: who can edit the person record itself (phone, name, stage,
  // org links, convert). Mirrors the backend canEditPerson — owner direct or
  // derived, manager, admin. Activity logging is no longer gated by this.
  const isDirectOwner = currentPerson.assignedUserId === getBookUserId(currentUser);
  const isDerivedOwner = currentPerson.mineSource === 'derived';
  // Unclaimed-open: a person nobody claims is editable by any rep, and by an
  // assistant who is acting for a rep — mirrors backend canEditPerson and the
  // unassigned-org rule on OrganizationDetail. An assistant with no active
  // assignment stays read-only.
  const assistant = isAssistantUser(currentUser);
  const isUnclaimedOpen =
    currentPerson.isUnclaimed === true &&
    (currentUser?.role === 'SALES_REP' || (assistant && !!currentUser?.actingFor));
  const canEdit =
    isDirectOwner ||
    isDerivedOwner ||
    isUnclaimedOpen ||
    currentUser?.role === 'MANAGER' ||
    currentUser?.role === 'ADMIN';

  // Activity logging is open to any authenticated user, except that an
  // assistant logs only on people she can edit.
  const canLogActivity = !assistant || canEdit;

  // CRM-110: who can raise a "coach no longer here" flag — record owner/
  // manager/admin (canEdit), plus inside sales / data steward / manager who
  // don't necessarily own the record but do phone verification work.
  const OPEN_FLAG_STATUSES = ['RAISED', 'ON_CALL_LIST', 'CONFIRMED', 'KICKED_BACK'];
  const openFlag = personFlags.find((f) => OPEN_FLAG_STATUSES.includes(f.status)) ?? null;
  const canFlagMove =
    (canEdit || hasAnyRole('INSIDE_SALES', 'DATA_STEWARD', 'MANAGER')) &&
    currentPerson.organizations.length > 0;
  // Non-owners get the same button but a notify-only path: the sheet sends a
  // heads-up Task to the assigned rep instead of raising a flag. Suppressed
  // while a heads-up is already open (they see the chip instead); the owner
  // keeps the button — their real raise auto-closes the heads-up.
  const canNotify = !canFlagMove && currentPerson.organizations.length > 0 && !openNotify;
  const flagSheetMode: 'raise' | 'notify' = canFlagMove ? 'raise' : 'notify';
  const notifyTargetName =
    openNotify?.assignedUser.displayName ??
    currentPerson.assignedUser?.displayName ??
    'the assigned rep';

  const openMerge = personMerges.find((m) => m.status === 'RAISED') ?? null;
  const canMerge = canEdit || hasAnyRole('INSIDE_SALES', 'DATA_STEWARD', 'MANAGER');

  // Banner: shown only when the viewer has no claim and the record has an
  // assigned rep (or is wholly unassigned) — informational, not restrictive.
  const bannerVariant: 'assigned-to-other' | 'unassigned' | null =
    currentPerson.mineSource !== null
      ? null
      : currentPerson.assignedUserId === null
        ? 'unassigned'
        : 'assigned-to-other';

  // Soft nudge for the activity modals — same logic as the banner.
  const assignmentNudge =
    bannerVariant === null
      ? null
      : {
          variant: bannerVariant,
          assigneeName: currentPerson.assignedUser?.displayName ?? undefined,
        };

  const personFullName = `${effectiveFirstName(currentPerson)} ${effectiveLastName(currentPerson)}`;

  // Get list of already-linked organization IDs (to exclude from search)
  const existingOrgIds = currentPerson.organizations.map((link) => link.organization.id);

  // Resolve primary org name for activity modal "Are you at [School]?" prompt
  const primaryOrgName = (() => {
    if (!currentPerson?.organizations?.length) return null;
    const orgs = currentPerson.organizations;
    // Deduplicate by organization.id
    const seen = new Set<string>();
    const unique = orgs.filter((o) => {
      if (seen.has(o.organization.id)) return false;
      seen.add(o.organization.id);
      return true;
    });
    // Find primary, else use first
    const primary = unique.find((o) => o.isPrimary) ?? unique[0];
    return primary?.organization.name ?? null;
  })();

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <PersonHeader
        person={currentPerson}
        onEdit={handleEdit}
        onLogActivity={handleLogActivity}
        onScheduleActivity={() => setIsScheduleModalOpen(true)}
        onConvert={isAssistantUser(currentUser) ? undefined : handleConvert}
        canEdit={canEdit}
        canLogActivity={canLogActivity}
        onFlagMove={canFlagMove || canNotify ? () => setIsFlagSheetOpen(true) : undefined}
        openFlagStatus={openFlag?.status ?? null}
        openNotifyInfo={openNotify ? { assigneeName: notifyTargetName } : null}
        onMerge={canMerge ? () => setIsMergeSheetOpen(true) : undefined}
        openMergeStatus={openMerge?.status ?? null}
      />

      {/* Tags — pill row + add-control (backend enforces apply permission) */}
      {currentPerson && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4">
          <div className="mt-2 space-y-2">
            <TagChips tags={personTags.filter((t) => !t.code?.startsWith('src-'))} onRemove={(t) => remove('PERSON', currentPerson.id, t)} />
            <div className="max-w-xs"><TagPicker kind="PERSON" entityId={currentPerson.id} /></div>
          </div>
          <div className="mt-4">
            <MergedFromSection merges={personMerges} personId={currentPerson.id} />
          </div>
        </div>
      )}

      {/* Assignment info banner (informational only, no lock-down) */}
      {bannerVariant && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <AssignmentInfoBanner
            variant={bannerVariant}
            assigneeName={currentPerson.assignedUser?.displayName ?? undefined}
          />
          <RequestCoachButton personId={currentPerson.id} canRequest={currentUser?.role === 'SALES_REP'} />
        </div>
      )}

      {/* Main content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left column - Contact & Pipeline */}
          <div className="lg:col-span-1 space-y-6">
            <PersonContactInfo
              person={currentPerson}
              onComposeEmail={() => setIsComposeOpen(true)}
              onOpenInGmail={gmailLaunch.launch}
            />
            {canEdit && <PersonPrivateNote person={currentPerson} canEdit={canEdit} />}
            <PersonPipelineStatus person={currentPerson} canEdit={canEdit} />
            <PersonBlastStatus person={currentPerson} />

            {/* Sources block — system lineage, read-only */}
            <div className="card p-4">
              <h3 className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">
                Sources
              </h3>
              {/* Origin badge */}
              <div className="flex items-center gap-2 mb-3">
                <span className="text-xs text-gray-500 dark:text-gray-400">Origin</span>
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300">
                  {CHANNEL_LABELS[currentPerson.source ?? ''] ?? 'Unknown'}
                </span>
              </div>
              {/* Source-tag chips (src-* codes, display-only, no remove control) */}
              {(() => {
                const srcTags = personTags.filter((t) => t.code?.startsWith('src-'));
                if (srcTags.length === 0) return null;
                return (
                  <div className="flex flex-wrap gap-1.5">
                    {srcTags.map((t) => (
                      <span
                        key={t.id}
                        title={t.description || t.name}
                        className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300"
                        style={t.color ? { backgroundColor: `${t.color}22`, color: t.color } : undefined}
                      >
                        {t.name}
                      </span>
                    ))}
                  </div>
                );
              })()}
            </div>
          </div>

          {/* Right column - Organizations, Activities, Tasks */}
          <div className="lg:col-span-2 space-y-6">
            {/* Organizations */}
            <PersonOrganizations
              person={currentPerson}
              onLinkOrganization={handleLinkOrganization}
              onLinksChanged={handleLinksChanged}
              canEdit={canEdit}
            />

            {/* Upcoming Activities (renders nothing when empty) */}
            <UpcomingActivities
              personId={currentPerson.id}
              onComplete={(activity) => setCompletingActivity(activity)}
              onCancel={(activityId) => {
                cancelActivity(currentPerson.id, activityId);
              }}
            />

            {/* Activity Timeline */}
            <div className="card p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Activity Timeline
                </h3>
                {canLogActivity && (
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setIsScheduleModalOpen(true)}
                      className="btn-secondary text-sm py-1.5"
                    >
                      <Calendar className="h-4 w-4 mr-1" />
                      Schedule
                    </button>
                    <button
                      onClick={handleLogActivity}
                      className="btn-secondary text-sm py-1.5"
                    >
                      <MessageSquare className="h-4 w-4 mr-1" />
                      Log Activity
                    </button>
                  </div>
                )}
              </div>
              <ActivityTimeline personId={currentPerson.id} />
            </div>

            {/* Tasks */}
            <PersonTasks person={currentPerson} canEdit={canEdit} />
          </div>
        </div>
      </div>

      {/* Activity modals — open to any authenticated user */}
      <AddActivityModal
        isOpen={isActivityModalOpen || !!completingActivity}
        onClose={() => {
          setIsActivityModalOpen(false);
          setCompletingActivity(null);
        }}
        personId={currentPerson.id}
        personName={personFullName}
        phoneNumbers={callableNumbers(currentPerson)}
        currentTemperature={currentPerson.temperature}
        organizationName={primaryOrgName}
        onActivityCreated={handleActivityCreated}
        assignmentNudge={assignmentNudge}
        prefill={
          completingActivity
            ? {
                type: completingActivity.type,
                summary: completingActivity.summary || undefined,
                completingActivityId: completingActivity.id,
              }
            : undefined
        }
      />

      <ScheduleActivityModal
        isOpen={isScheduleModalOpen}
        onClose={() => setIsScheduleModalOpen(false)}
        personId={currentPerson.id}
        personName={personFullName}
        onScheduled={() => {
          if (id) {
            fetchUpcoming(id);
          }
        }}
        assignmentNudge={assignmentNudge}
      />

      <CoachNoLongerHereSheet
        key={isFlagSheetOpen ? 'open' : 'closed'}
        isOpen={isFlagSheetOpen}
        onClose={() => setIsFlagSheetOpen(false)}
        person={currentPerson}
        onSuccess={() => { refreshFlags(); if (id) fetchPerson(id); }}
        mode={flagSheetMode}
        notifyTargetName={notifyTargetName}
        initial={flagSheetMode === 'raise' && openNotify?.meta ? openNotify.meta : undefined}
      />

      <MergePersonSheet
        key={isMergeSheetOpen ? 'merge-open' : 'merge-closed'}
        isOpen={isMergeSheetOpen}
        onClose={() => setIsMergeSheetOpen(false)}
        person={currentPerson}
        onSuccess={() => { refreshMerges(); if (id) fetchPerson(id); }}
      />

      {currentPerson && (
        <ComposeEmailSheet
          isOpen={isComposeOpen}
          personId={currentPerson.id}
          toEmail={effectiveEmail(currentPerson) ?? ''}
          canSend={canSend}
          onSent={(activity) => useActivityStore.getState().prependActivity(activity)}
          onClose={() => setIsComposeOpen(false)}
        />
      )}

      {currentPerson && (
        <GmailFallbackDialog
          open={gmailLaunch.fallbackOpen}
          email={contactEmail}
          onComposeInApp={() => {
            gmailLaunch.closeFallback();
            setIsComposeOpen(true);
          }}
          onClose={gmailLaunch.closeFallback}
        />
      )}

      {/* Record-edit modals — owner / manager / admin only */}
      {canEdit && (
        <>
          <PersonForm
            person={currentPerson}
            isOpen={isEditFormOpen}
            onClose={handleEditClose}
            onSuccess={handleEditSuccess}
          />

          <LinkOrganizationModal
            isOpen={isLinkOrgModalOpen}
            onClose={handleLinkOrgModalClose}
            personId={currentPerson.id}
            personName={personFullName}
            existingOrgIds={existingOrgIds}
            onSuccess={handleLinksChanged}
          />

          <ConvertToBlastModal
            isOpen={isConvertModalOpen}
            onClose={() => setIsConvertModalOpen(false)}
            person={currentPerson}
            onSuccess={handleConvertSuccess}
          />
        </>
      )}
    </div>
  );
};
